import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Feature } from 'src/app/modules/cop/drawing/features/feature.model';
import { LayerMapFeatures } from 'src/app/modules/cop/drawing/models/layer-map-features.model';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { AlertFlightFeature } from '../features/alert-flight/alert-flight.feature';
import { LayerRenderer } from '../models/layer-renderer.model';
import { MapDrawingContext } from '../models/map-drawing-context.model';

export const MAX_DOCS_TO_RENDER = 5000;

export class AlertsFlightsLayerRenderer implements LayerRenderer {
  private addDocuments$: Subject<{ drawingContext: MapDrawingContext; layer: Layer; documents: Document[]; total: number }> = new Subject();
  private updateDocuments$: Subject<{ drawingContext: MapDrawingContext; layer: Layer; documents: Document[]; total: number }> = new Subject();
  private removeDocuments$: Subject<{ drawingContext: MapDrawingContext; layer: Layer; documentUIds: string[] }> = new Subject();

  constructor() {
    this.addDocuments$.pipe(tap(data => this.processAddDocuments(data.drawingContext, data.layer, data.documents, data.total))).subscribe();
    this.updateDocuments$.pipe(tap(data => this.processUpdateDocuments(data.drawingContext, data.layer, data.documents, data.total))).subscribe();
    this.removeDocuments$.pipe(tap(data => this.processRemoveDocuments(data.drawingContext, data.layer, data.documentUIds))).subscribe();
  }

  addDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    for (let i = 0; i < documents.length; i += MAX_DOCS_TO_RENDER) {
      const documentChunk = documents.slice(i, i + MAX_DOCS_TO_RENDER);
      setTimeout(() => this.addDocuments$.next({ drawingContext, layer, documents: documentChunk, total: documents.length }), 0);
    }
  }

  processAddDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[], total: number): void {
    // init the layer feature set if needed
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    for (let d of documents) {
      this.renderDocument(drawingContext, layer, d, layerFeatures);
    }

    if (total - Object.keys(layerFeatures).length < MAX_DOCS_TO_RENDER) {
      drawingContext.onLoadingDone(layer);
    }
   }

  updateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    for (let i = 0; i < documents.length; i += MAX_DOCS_TO_RENDER) {
      const documentChunk = documents.slice(i, i + MAX_DOCS_TO_RENDER);
      setTimeout(() => this.updateDocuments$.next({ drawingContext, layer, documents: documentChunk, total: documents.length }), 0);
    }
  }

  processUpdateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[], total: number): void {
    // init the layer feature set if needed
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    for (let d of documents) {
      this.renderDocument(drawingContext, layer, d, layerFeatures);
    }

    if (total - Object.keys(layerFeatures).length < MAX_DOCS_TO_RENDER) {
      drawingContext.onLoadingDone(layer);
    }
  }

  removeDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds: string[]): void {
    if (!documentUIds) {
      return;
    }

    for (let i = 0; i < documentUIds?.length; i += MAX_DOCS_TO_RENDER) {
      const documentChunk = documentUIds.slice(i, i + MAX_DOCS_TO_RENDER);
      setTimeout(() => this.removeDocuments$.next({ drawingContext, layer, documentUIds: documentChunk }), 0);
    }
  }

  processRemoveDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds?: string[]): void {
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      return;
    }

    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    const documentUIdsToRemove = documentUIds ?? Object.keys(layerFeatures);
    for (let uid of documentUIdsToRemove) {
      this.removeDocument(drawingContext, layer, uid, layerFeatures);
    }
  }

  removeDocument(drawingContext: MapDrawingContext, layer: Layer, uid: string, layerFeatures: LayerMapFeatures): void {
    const docFeatures = layerFeatures[uid];
    if (!!!docFeatures) {
      return;
    }

    docFeatures?.features.forEach(feature => {
      feature.destroy();
    });

    delete layerFeatures[uid];
  }

  renderDocument(drawingContext: MapDrawingContext, layer: Layer, document: Document, layerFeatures: LayerMapFeatures): void {
    // init the document feature set if needed
    if (!!!layerFeatures[document.uid]) {
      layerFeatures[document.uid] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        features: [],
        hash: null
      };
    }

    // get a reference to the feature that is associated with the
    // current document. Create it, if it does not already exist.
    const docFeatures = layerFeatures[document.uid];
    let feature: Feature = null;
    if (docFeatures.features?.length > 0) {
      feature = docFeatures.features[0];
    } else {
      feature = new AlertFlightFeature();
      docFeatures.features.push(feature);
    }

    const options = {
      layer,
      document
    };

    feature.render(drawingContext, options);
  }
}
